"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Crown, Shield, Sword } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LEmpereurPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">L'Empereur</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Major Arkana'ya Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-xl p-8 mb-8 border border-red-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-l-empereur.svg"
                  alt="L'Empereur Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-red-900/40 to-orange-900/40 border border-red-500/30 flex-col items-center justify-center text-red-300">
                  <span className="text-3xl font-bold mb-2">IV</span>
                  <span className="text-lg font-semibold">L'EMPEREUR</span>
                  <span className="text-sm text-center mt-2">The Emperor</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">IV</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">L'Empereur</h1>
                  <p className="text-xl text-red-400">İmparator / Otorite ve Güç</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde dördüncü numara. Tahtında oturan İmparator, otorite, liderlik
                ve erkil gücü temsil eder. Düzen, yapı ve kontrol kartıdır.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Otorite</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Liderlik</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Düzen</span>
                <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">Kontrol</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Crown className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Güçlü Liderlik</h3>
                  <p className="text-slate-400 text-sm">Otorite sahibi olma, karar verme ve yöneticilik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yapı ve Düzen</h3>
                  <p className="text-slate-400 text-sm">Sistematik yaklaşım, organize olma ve kurallara uyma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Koruyuculuk</h3>
                  <p className="text-slate-400 text-sm">Baba figürü, koruma ve güvenlik sağlama.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Pratik Zeka</h3>
                  <p className="text-slate-400 text-sm">Stratejik düşünme, planlama ve uygulama yetisi.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sword className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Tiranlık</h3>
                  <p className="text-slate-400 text-sm">Aşırı otoriter yaklaşım, diktatörlük ve baskıcı davranış.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Katılık</h3>
                  <p className="text-slate-400 text-sm">Esneklik eksikliği, değişime direnç ve dogmatizm.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Otorite Kaybı</h3>
                  <p className="text-slate-400 text-sm">Güç kaybı, kontrolsüzlük ve liderlik eksikliği.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aşırı Dominantlık</h3>
                  <p className="text-slate-400 text-sm">Baskıcı tutum, egemenlik kurma ve özgürlük kısıtlama.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">İmparatorluk Tacı</h3>
              <p className="text-slate-400 text-sm">Üst otorite, kral gücü ve yöneticilik.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👑</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Taht</h3>
              <p className="text-slate-400 text-sm">Sağlam otorite, güç ve konumu temsil eder.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kalkan</h3>
              <p className="text-slate-400 text-sm">Koruma, savunma ve güvenlik sağlama.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⚔️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Asa/Asker Sembolleri</h3>
              <p className="text-slate-400 text-sm">Askeri güç, disiplin ve savaşçılık.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🏛️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Mimari</h3>
              <p className="text-slate-400 text-sm">Yapı, organizasyon ve kalıcılık.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">♂</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Mars Enerjisi</h3>
              <p className="text-slate-400 text-sm">Erkil güç, aksiyoner enerji ve rekabet.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Dominant partner</li>
                <li>• Koruyucu sevgili</li>
                <li>• Güçlü bağlılık</li>
                <li>• Baba figürü rolünde</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yöneticilik pozisyonları</li>
                <li>• Kurumsal liderlik</li>
                <li>• Devlet işi</li>
                <li>• Askeri kariyer</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Crown className="h-5 w-5 text-red-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Liderlik yetenekleri</li>
                <li>• Disiplin geliştirme</li>
                <li>• Otorite kazanma</li>
                <li>• Karakter gücü</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-400" />
                Manevi Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ruhsal otorite</li>
                <li>• Manevi disiplin</li>
                <li>• İç güç geliştirme</li>
                <li>• Koruyucu enerji</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/l-imperatrice"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: L'Impératrice
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/le-pape"
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Le Pape
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}